import type { TamsEssenceParameters, TamsGetUrlsEntry, TamsSourceCollectionEntry, TamsClient, TamsFlow, TamsFormatType, TamsSegment, TamsSegmentsParameters, TamsSource } from '../types/tams';
import type { BasicFetch } from '../types/basicFetch';
import type { Logger } from '../types/logger';
import { fetchOrThrow } from '../io/fetchOrThrow';
import isObject from 'lodash/isObject';
import type { ValidationFunction, ValidationResult } from '../util/validation';
import { makeValidationFunction, validateObjectArray, validateObjectProperty, validateOptionalArrayProperty, validateOptionalNumberProperty, validateOptionalObjectProperty, validateOptionalStringProperty, validateStringProperty } from '../util/validation';
import { validateFraction } from '../util/integerMath';
import { notUndefined } from '../portable/basicTypes';
import { Lock } from '../async/lock';


export interface DefaultTamsClientConfig {
  apiUrl: string,
  accessToken: string,
}

export function createDefaultTamsClient(
  services: {
    fetch: BasicFetch,
    logger: Logger,
  },
  configProvider: () => Promise<DefaultTamsClientConfig>,
): TamsClient {
  const tamsFetch = new TamsFetch(services, configProvider);

  return {
    async getSources(): Promise<TamsSource[]> {
      return await tamsFetch.get('/sources', validateTamsSources);
    },
    async getSource(sourceId: string): Promise<TamsSource | null> {
      return await tamsFetch.get('/sources/' + sourceId, validateTamsSource);
    },
    async getFlows(): Promise<TamsFlow[]> {
      return await tamsFetch.get('/flows', validateTamsFlows);
    },
    async getFlow(flowId: string): Promise<TamsFlow | null> {
      return await tamsFetch.get('/flows/' + flowId, validateTamsFlow);
    },
    async getSegments(parameters: TamsSegmentsParameters): Promise<TamsSegment[]> {
      const {
        flowId,
        accept_get_urls,
        timerange,
        limit,
        object_id,
      } = parameters;
      return await tamsFetch.get(
        '/flows/' + flowId + '/segments',
        validateTamsSegments,
        {
          accept_get_urls,
          timerange,
          limit,
          object_id,
        }
      );
    },
  };
}

class TamsFetch {
  private lock : Lock = new Lock();
  private config : DefaultTamsClientConfig | null = null;

  public constructor(
    private services: {
      fetch: BasicFetch,
      logger: Logger,
    },
    private configProvider: () => Promise<DefaultTamsClientConfig>
  ) {
  }

  public async get<T>(
    path: string,
    responseValidator: ValidationFunction<T>,
    query?: Record<string, string | number | undefined>,
  ): Promise<T> {
    const { logger, fetch } = this.services;


    return await this.withConfig(async (config) => {
      const { apiUrl, accessToken } = config;
      const url = apiUrl + path + serializeQueryString(query);
      logger.info(`Fetch: GET ${url}`);

      const response =
        await fetchOrThrow(
          logger,
          fetch,
          url,
          {
            method: 'GET',
            headers: [
                ['Authorization', `Bearer ${accessToken}`],
            ],
          }
        );

      if (!response.ok) {
        throw new Error(`Fetch to TAMS server failed: ${response.statusText}`);
      }

      return validateResponseOrThrow(await response.json(), responseValidator);
    });
  }

  private async withConfig<T>(callback: (config: DefaultTamsClientConfig) => Promise<T>): Promise<T> {
    while (true) {
      try {
        await this.lock.waitForLock();
        if (this.config === null) {
          this.config = await this.configProvider();
        }
      } finally {
        this.lock.releaseLock();
      }

      try {
        return await callback(this.config);
      } catch (err) {
        if (this.isTokenExpiredError(err)) {
          this.config = null;
          continue;
        }
        throw err;
      }
    }
  }

  isTokenExpiredError(_err: any): boolean {
    // TODO: Figure out how to know this.
    return false;
  }
}

function serializeQueryString(query: Record<string, string | number | undefined> | undefined): string {
  if (query === undefined) {
    return '';
  }
  const sanitized =
    Object.entries(query).filter(([_key, value]) => notUndefined(value));

  if (sanitized.length === 0) {
    return '';
  }

  // TODO: Should probably URL escape this
  return '?' + sanitized.map(([key, value]) => `${key}=${value}`).join('&');
}

function validateResponseOrThrow<T>(
  payload: unknown,
  validationFunction: ValidationFunction<T>
): T {
  const result = validationFunction(payload);
  if (result.errorMsg !== null) {
    throw new Error(`Response validation failed: ${result.errorMsg}`);
  }
  return result.value;
}

export type TamsOAuthScope = 'tams-api/read' | 'tams-api/write' | 'tams-api/delete';

export function createOAuthDefaultTamsClientProvider(
  services: {
    fetch: BasicFetch,
    logger: Logger,
  },
  config: {
    clientId: string,
    clientSecret: string,
    tokenUrl: string,
    apiUrl: string,
    oauthScopes: TamsOAuthScope[],
  }
): (() => Promise<DefaultTamsClientConfig>) {
  return async () => {
    const formData = encodeFormData({
      client_id: config.clientId,
      client_secret: config.clientSecret,
      grant_type: 'client_credentials',
      scope: config.oauthScopes.join(' '),
    });

    const { fetch, logger } = services;

    const response =
      await fetchOrThrow(
        logger,
        fetch,
        config.tokenUrl,
        {
          method: 'POST',
          headers: [
              ['Content-Type', 'application/x-www-form-urlencoded'],
          ],
          body: formData,
        }
      );

    if (!response.ok) {
      throw new Error(`Failed to fetch token: ${response.statusText}`);
    }

    const tokenInfo = await response.json();
    return {
      apiUrl: config.apiUrl,
      accessToken: tokenInfo.access_token,
    };
  };
}

function encodeFormData(data: Record<string, string>) {
  return Object.keys(data)
      .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
      .join('&');
}

function validateTamsFormat(u: unknown): ValidationResult<TamsFormatType> {
  switch (u) {
    case 'urn:x-nmos:format:video':
    case 'urn:x-nmos:format:audio':
    case 'urn:x-nmos:format:data':
    case 'urn:x-tam:format:image':
    case 'urn:x-nmos:format:multi':
      return { errorMsg: null, value: u };
    default:
      return { errorMsg: `Invalid TAMS format: ${JSON.stringify(u)}` };
  }
}

function validateTags(u: unknown): ValidationResult<Record<string, string>> {
  if (!isObject(u)) {
    return { errorMsg: 'Not an object' };
  }
  if (u === undefined) {
    return { errorMsg: 'Is undefined' };
  }
  const o = u as Record<string, unknown>;

  const stringMap : Record<string, string> = {};
  for (const key in o) {
    if (typeof key !== 'string') {
      return { errorMsg: 'One of the keys is not of type "string"' };
    }
    const value = o[key];
    if (typeof value !== 'string') {
      return { errorMsg: `Value "${JSON.stringify(value)}" is not of type "string"` };
    }
    stringMap[key] = value;
  }
  return { errorMsg: null, value: stringMap };
}

const validateTamsSourceCollectionEntry =
  makeValidationFunction<TamsSourceCollectionEntry>((u) => {
    const tamsSourceCollectionEntry : TamsSourceCollectionEntry = {
      id: validateStringProperty(u, 'id'),
      role: validateStringProperty(u, 'role'),
    };
    return tamsSourceCollectionEntry;
  });

const validateTamsSource =
  makeValidationFunction<TamsSource>((u) => {
    const tamsSource : TamsSource = {
      id: validateStringProperty(u, 'id'),
      format: validateObjectProperty(u, 'format', 'Format', validateTamsFormat),
      label: validateOptionalStringProperty(u, 'label'),
      description: validateOptionalStringProperty(u, 'description'),
      created_by: validateOptionalStringProperty(u, 'created_by'),
      updated_by: validateOptionalStringProperty(u, 'updated_by'),
      tags: validateOptionalObjectProperty(u, 'tags', 'Tags', validateTags),
      source_collection: validateOptionalArrayProperty(u, 'source_collection', 'Source Collection', validateTamsSourceCollectionEntry),
    };
    return tamsSource;
  });

const validateTamsSources =
  makeValidationFunction<TamsSource[]>((u) => {
    return validateObjectArray(u, 'TamsSource', validateTamsSource);
  });

const validateEssenceParameters =
  makeValidationFunction<TamsEssenceParameters>((u) => {
    const tamsEssenceParameters : TamsEssenceParameters = {
      frame_rate: validateOptionalObjectProperty(u, 'frame_rate', 'Fraction', validateFraction),
      frame_width: validateOptionalNumberProperty(u, 'frame_width'), // required according to specs
      frame_height: validateOptionalNumberProperty(u, 'frame_height'), // required according to specs
    };
    return tamsEssenceParameters;
  });

const validateTamsFlow =
  makeValidationFunction<TamsFlow>((u) => {
    const tamsFlow : TamsFlow = {
      id: validateStringProperty(u, 'id'),
      format: validateObjectProperty(u, 'format', 'Format', validateTamsFormat),
      source_id: validateStringProperty(u, 'source_id'),
      label: validateOptionalStringProperty(u, 'label'),
      description: validateOptionalStringProperty(u, 'description'),
      created_by: validateOptionalStringProperty(u, 'created_by'),
      updated_by: validateOptionalStringProperty(u, 'updated_by'),
      codec: validateOptionalStringProperty(u, 'codec'), // required according to the specs
      container: validateOptionalStringProperty(u, 'container'),
      segment_duration: validateOptionalObjectProperty(u, 'segment_duration', 'Fraction', validateFraction),
      tags: validateOptionalObjectProperty(u, 'tags', 'Tags', validateTags),
      timerange: validateOptionalStringProperty(u, 'timerange'),
      flow_collection: validateOptionalArrayProperty(u, 'flow_collection', 'FlowCollectionEntry', validateTamsSourceCollectionEntry),
      essence_parameters: validateOptionalObjectProperty(u, 'essence_parameters', 'EssenceParameters', validateEssenceParameters),
    };
    return tamsFlow;
  });

const validateTamsFlows =
  makeValidationFunction<TamsFlow[]>((u) => {
    return validateObjectArray(u, 'TamsFlow', validateTamsFlow);
  });

const validateTamsGetUrlsEntry =
  makeValidationFunction<TamsGetUrlsEntry>((u) => {
    return {
      label: validateStringProperty(u, 'label'),
      url: validateStringProperty(u, 'url'),
    };
  });

const validateTamsSegment =
  makeValidationFunction<TamsSegment>((u) => {
    return {
      object_id: validateStringProperty(u, 'object_id'),
      timerange: validateStringProperty(u, 'timerange'),
      get_urls: validateOptionalArrayProperty(u, 'get_urls', 'TamsGetUrlsEntry', validateTamsGetUrlsEntry)
    };
  });

const validateTamsSegments =
  makeValidationFunction<TamsSegment[]>((u) => {
    return validateObjectArray(u, 'TamsSegment', validateTamsSegment);
  });

